import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ShieldCheck } from 'lucide-react';

import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { toast } from '@/stores/toast.store';
import { apiCall, ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

/**
 * F5-T8 · Uploader 03, capa B — «Captura tardía del ID de Medicaid»
 * (contrato 2026-08-28). El endpoint y las props son los vivos (Cluster N:
 * POST /uploader/clients/:cid/capture-cin, TX atómica withPhi); cambia la
 * piel y el contrato del campo:
 *
 *  - Formato NY validado EN VIVO: ^[A-Z]{2}[0-9]{5}[A-Z]$ — HOTFIX
 *    2026-08-30: la regex original exigía «dos letras y siete dígitos», un
 *    formato que NO existe — el survey enmascarado de prod (ver
 *    agent-queue.service.ts) mide 468.393 CIN reales con forma AA99999A
 *    (2 letras + 5 dígitos + 1 letra). El botón «Guardar» jamás se activaba
 *    con un ID real (caminata del CEO, prod). Normalización uppercase +
 *    strip no-alfanumérico antes de validar. La pista (aria-live) dice
 *    exactamente qué falta, incluida la letra final.
 *  - ORIGEN OBLIGATORIO (muere «guardar sin origen»): 3 radio-cards
 *    cerradas (expediente / findhelp / agente). SOLO UI — el body del
 *    endpoint no lo acepta: PERSISTIR el origen declarado (columna o
 *    metadata del audit) NECESITA SERVER; por eso el copy de auditoría dice
 *    lo que HOY es verdad (actor + hora) y no promete el origen.
 *  - ID duplicado (409 DUPLICATE_CIN) = «el error más caro»: no se guarda,
 *    caja SIN ROJO (vetado en esta capa) y UN SOLO botón «Devolver el
 *    screening». Mostrar el dueño enmascarado NECESITA SERVER (el 409 no
 *    trae el nombre).
 *  - «Devolver el screening en vez de esto» (ghost, izquierda) cierra este
 *    modal: la vía de devolución vive en el modal de revisión (Uploader 02),
 *    que queda debajo.
 *  - El modal escribe UN SOLO campo (murió la edición completa de la ficha).
 */
export interface CinLateCaptureModalProps {
  open: boolean;
  clientId: string;
  hmId: string;
  /** Display name to show in modal header (e.g. "Garcia · HH_523"). */
  clientLabel: string;
  /** Refetch queryKey to invalidate on success (e.g. screening detail). */
  invalidateQueryKey?: readonly unknown[];
  onClose: () => void;
}

export function CinLateCaptureModal(props: CinLateCaptureModalProps): JSX.Element {
  useBodyScrollLock(props.open);
  return createPortal(
    <AnimatePresence>
      {props.open ? <Inner {...props} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

type Origin = 'expediente' | 'findhelp' | 'agente';

const CIN_RE = /^[A-Z]{2}[0-9]{5}[A-Z]$/;

type CinState =
  | { kind: 'empty' }
  | { kind: 'valid' }
  | { kind: 'letters' }
  | { kind: 'digits'; missing: number }
  | { kind: 'tail' }
  | { kind: 'extra' };

function analyzeCin(norm: string): CinState {
  if (norm.length === 0) return { kind: 'empty' };
  if (CIN_RE.test(norm)) return { kind: 'valid' };
  // Forma real AA99999A: letras iniciales, dígitos, letra final.
  const m = /^([A-Z]*)([0-9]*)([A-Z]*)$/.exec(norm);
  if (!m) return { kind: 'extra' }; // orden roto (dígito tras la letra final)
  const letters = (m[1] ?? '').length;
  const digits = (m[2] ?? '').length;
  const tail = (m[3] ?? '').length;
  if (letters < 2) return { kind: 'letters' };
  if (letters > 2 || digits > 5 || tail > 1) return { kind: 'extra' };
  if (digits < 5) {
    // letra final antes de completar los dígitos = orden roto, no «faltan».
    if (tail > 0) return { kind: 'extra' };
    return { kind: 'digits', missing: 5 - digits };
  }
  return { kind: 'tail' }; // 2 letras + 5 dígitos, falta la letra final
}

function Inner({ clientId, hmId, clientLabel, invalidateQueryKey, onClose }: CinLateCaptureModalProps): JSX.Element {
  const t = useT();
  const reduce = useReducedMotion();
  const qc = useQueryClient();
  const [medicaidId, setMedicaidId] = useState('');
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [dup, setDup] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (mid: string) =>
      apiCall<{ ok: true; clientId: string; cinLast6: string }>(
        `/uploader/clients/${encodeURIComponent(clientId)}/capture-cin`,
        { method: 'POST', body: { medicaidId: mid, hmId } },
      ),
    onSuccess: () => {
      if (invalidateQueryKey) {
        void qc.invalidateQueries({ queryKey: invalidateQueryKey });
      }
      toast.success(t('cinCapture.toast.success'));
      onClose();
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const state = analyzeCin(medicaidId);
  const valid = state.kind === 'valid';
  const canSubmit = valid && origin != null && !mut.isPending && !dup;
  const disabledWhy = !valid
    ? t('f5t8.cin.whyFormat')
    : origin == null
      ? t('f5t8.cin.whyOrigin')
      : null;

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (!canSubmit) return;
    try {
      await mut.mutateAsync(medicaidId);
    } catch (e) {
      const payload = (e instanceof ApiError ? e.payload : null) as
        | { code?: string }
        | null;
      if (payload?.code === 'DUPLICATE_CIN') {
        // «El error más caro»: no se guarda; única salida = devolver.
        setDup(true);
      } else {
        setError(e instanceof ApiError ? e.message : (e as Error).message || 'Capture failed');
      }
    }
  };

  const backdrop: Variants = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } }, exit: { opacity: 0, transition: { duration: 0.15 } } };
  const modal: Variants = reduce
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 }, exit: { opacity: 1, y: 0 } }
    : {
        // fadeUp 450ms del contrato (translateY 10px→0).
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
        exit: { opacity: 0, y: 6, transition: { duration: 0.15 } },
      };

  const inputBorder =
    state.kind === 'empty' ? 'border-nav-line'
    : state.kind === 'valid' ? 'border-nav-sage'
    : 'border-nav-amber';
  const hintColor =
    state.kind === 'empty' ? 'text-nav-slate'
    : state.kind === 'valid' ? 'text-nav-sageText'
    : 'text-nav-amberText';
  const hintText =
    state.kind === 'empty' ? t('f5t8.cin.hint.neutral')
    : state.kind === 'valid' ? t('f5t8.cin.hint.valid')
    : state.kind === 'letters' ? t('f5t8.cin.hint.letters')
    : state.kind === 'extra' ? t('f5t8.cin.hint.extra')
    : state.kind === 'tail' ? t('f5t8.cin.hint.tail')
    : state.missing === 1 ? t('f5t8.cin.hint.digits1')
    : t('f5t8.cin.hint.digitsN', { n: state.missing });

  const origins: Array<{ key: Origin; title: string; desc: string }> = [
    { key: 'expediente', title: t('f5t8.cin.origin.expediente'), desc: t('f5t8.cin.origin.expedienteDesc') },
    { key: 'findhelp', title: t('f5t8.cin.origin.findhelp'), desc: t('f5t8.cin.origin.findhelpDesc') },
    { key: 'agente', title: t('f5t8.cin.origin.agente'), desc: t('f5t8.cin.origin.agenteDesc') },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
      <motion.div
        className="fixed inset-0 bg-nav-navy/50 backdrop-blur-sm"
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t('f5t8.cin.aria', { name: clientLabel })}
        className="relative bg-white border-[1.5px] border-nav-card rounded-[14px] shadow-[0_24px_60px_rgba(18,43,68,.14)] w-full max-w-[600px] pt-[26px] px-[28px]"
        variants={modal}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-head text-[19px] font-bold tracking-[-0.01em] text-nav-navy">
            {t('f5t8.cin.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('f5t8.cin.closeAria')}
            className="w-[44px] h-[44px] -mt-2 -mr-2 flex-none inline-flex items-center justify-center rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
          >
            <X className="w-[18px] h-[18px]" aria-hidden />
          </button>
        </div>

        <p className="font-body text-[13.5px] text-nav-slate mt-1 mb-5 tabular-nums">
          {t('f5t8.cin.body', { name: clientLabel })}
        </p>

        <label htmlFor="cin-input" className="block font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate mb-1.5">
          {t('f5t8.cin.label')}
        </label>
        <input
          id="cin-input"
          type="text"
          value={medicaidId}
          onChange={(e) => {
            // Normalización del contrato: uppercase + strip no-alfanumérico.
            setMedicaidId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15));
            setDup(false);
          }}
          placeholder="NY0000000"
          maxLength={15}
          aria-label={t('f5t8.cin.label')}
          aria-describedby="id-hint"
          className={cn(
            'w-full h-[52px] px-[15px] rounded-lg border-[1.5px] bg-white font-head text-[18px] font-bold tracking-[.06em] tabular-nums text-nav-navy',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
            'transition-colors duration-150 motion-reduce:transition-none placeholder:text-nav-faint',
            inputBorder,
          )}
          autoFocus
          disabled={dup}
        />
        <p id="id-hint" aria-live="polite" className={cn('font-body text-[12.5px] leading-relaxed mt-[9px] mb-5', hintColor)}>
          {hintText}
        </p>

        {!dup && (
          <div className="rounded-[12px] border-[1.5px] border-nav-card bg-nav-softer p-4">
            <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate mb-2.5">
              {t('f5t8.cin.originLabel')}
            </p>
            <div className="flex flex-col gap-2">
              {origins.map((o) => {
                const sel = origin === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    aria-pressed={sel}
                    onClick={() => setOrigin(o.key)}
                    className={cn(
                      'w-full min-h-[44px] flex items-start gap-3 text-left rounded-[10px] border-[1.5px] py-[13px] px-[15px] transition-colors duration-150 motion-reduce:transition-none active:scale-[0.99]',
                      'hover:border-nav-teal hover:bg-[#f4fafa]',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
                      sel ? 'border-nav-teal bg-[#f4fafa]' : 'border-nav-card bg-white',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-px w-[18px] h-[18px] flex-none rounded-full border-[1.5px] inline-flex items-center justify-center',
                        sel ? 'border-nav-teal' : 'border-nav-line',
                      )}
                      aria-hidden
                    >
                      {sel && <span className="w-[9px] h-[9px] rounded-full bg-nav-teal" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-head text-[13.5px] font-bold text-nav-navy">{o.title}</span>
                      <span className="block font-body text-[12.5px] text-nav-slate mt-0.5">{o.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Aviso de auditoría — dice lo que HOY es verdad (actor + hora). */}
        {!dup && (
          <div className="flex items-start gap-2.5 rounded-[12px] border-[1.5px] border-[#c9e2e2] bg-nav-tealTint py-[15px] px-[17px] mt-4 text-nav-tealDark">
            <ShieldCheck className="h-[17px] w-[17px] flex-none mt-px" aria-hidden />
            <p className="font-body text-[13px] leading-relaxed">{t('f5t8.cin.audit')}</p>
          </div>
        )}

        {/* ID ya usado por otro paciente — sin rojo; única salida: devolver. */}
        {dup && (
          <div className="rounded-[12px] border-[1.5px] border-nav-amber bg-nav-amberTint py-[15px] px-[17px] text-nav-amberText" role="status">
            <p className="font-head text-[13.5px] font-bold">{t('f5t8.cin.dupTitle')}</p>
            <p className="font-body text-[13px] leading-relaxed mt-1">{t('f5t8.cin.dupBody')}</p>
          </div>
        )}

        {error && !dup && (
          <p className="font-body text-[12.5px] text-nav-amberText mt-3" role="status">{error}</p>
        )}

        {/* PIE — chasis de la familia: salida a la izquierda, UN primario a la
            derecha; compensa el padding del modal (contrato capa B). */}
        <footer className="-mx-[28px] mt-6 px-[28px] py-[18px] border-t border-nav-hair bg-nav-softer rounded-b-[14px]">
          {dup ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] px-5 rounded-lg bg-nav-navy text-white font-head text-[13.5px] font-bold hover:bg-nav-navyHover transition-colors duration-150 active:scale-[0.98]"
              >
                {t('f5t8.cin.dupReturn')}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2.5 flex-wrap">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={mut.isPending}
                  className="min-h-[44px] px-[18px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-nav-hair transition-colors duration-150 active:scale-[0.98]"
                >
                  {t('f5t8.cin.return')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className={cn(
                    'inline-flex items-center gap-1.5 min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[13.5px] font-bold transition-colors duration-150 active:scale-[0.98]',
                    canSubmit
                      ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                      : 'bg-nav-hair border-nav-card text-[#9aa7b2] cursor-not-allowed',
                  )}
                >
                  {mut.isPending && <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden />}
                  {t('f5t8.cin.save')}
                </button>
              </div>
              {/* Diagnóstico del deshabilitado: dice cuál de los dos falta. */}
              {disabledWhy && (
                <p className="font-body text-[12.5px] text-nav-slate text-right mt-2">{disabledWhy}</p>
              )}
            </>
          )}
        </footer>
      </motion.div>
    </div>
  );
}

/** Returns the hmId parsed from a placeholder CIN like "HH_523", or null if not placeholder. */
export function parsePlaceholderCin(cin: string | null | undefined): string | null {
  if (!cin) return null;
  const m = /^HH_(\d+)$/.exec(cin);
  return m ? m[1]! : null;
}
